import { jsx as _jsx } from "react/jsx-runtime";
import { useCombobox } from 'downshift';
import { createContext, useCallback, useContext, useEffect, useRef, useState, useMemo, } from 'react';
import { useVirtual } from 'react-virtual';
import { generateTimeOptions, itemToString, getHighlightedIndex, parseInputToMinutes, formatMinutesToTime, parseInitialValue, formatMinutesToHHmmss, isFullyQualifiedTime, isValidTimeFormat, } from '../BKJTimeInput.utils.js';
import { useBKJTimeInputContextHooks } from './BKJTimeInput.context.hooks.js';
export const BKJTimeInputContext = createContext({});
export const BKJTimeInputProvider = ({ label, variant, placeholder, onChange, name, value, error, action, portalTargetElement, leftIconProps, leftIconName, disabled, required, children, is24hFormat = false, onIsOpenChange: __onIsOpenChange__, }) => {
    const [{ inputValue, anchorElement, dropdownElement }, { setInputValue, setAnchorElement, setDropdownElement },] = useBKJTimeInputContextHooks(parseInitialValue(value, is24hFormat));
    const [isDropdownReverse, setIsDropdownReverse] = useState(false);
    const skipBlurValidation = useRef(false);
    const hasNavigatedWithArrows = useRef(false);
    const pendingCursorPosition = useRef(null);
    const options = useMemo(() => generateTimeOptions(is24hFormat), [is24hFormat]);
    const customTimeOption = useMemo(() => {
        if (!inputValue || !isFullyQualifiedTime(inputValue))
            return null;
        const mins = parseInputToMinutes(inputValue, is24hFormat);
        if (mins === null)
            return null;
        const formattedLabel = formatMinutesToTime(mins, is24hFormat);
        const existingOption = options.find((opt) => opt.minutes === mins);
        if (existingOption)
            return null;
        return {
            id: 'custom-time',
            label: formattedLabel,
            value: formattedLabel,
            minutes: mins,
        };
    }, [inputValue, options, is24hFormat]);
    const parentRef = useRef(null);
    const estimateSize = useCallback(() => 56, []);
    const rowVirtualizer = useVirtual({
        size: options.length,
        parentRef,
        estimateSize,
        overscan: 2,
    });
    const { reset, isOpen, openMenu, closeMenu, toggleMenu, selectedItem, selectItem, getMenuProps, getItemProps, getInputProps, getLabelProps, getComboboxProps, highlightedIndex, setHighlightedIndex, getToggleButtonProps, setInputValue: __downShiftSetInputValue__, } = useCombobox({
        inputValue,
        itemToString,
        items: options,
        selectedItem: useMemo(() => {
            if (!value || value.length === 0)
                return null;
            const found = options.find((opt) => opt.value === value[0]);
            if (found)
                return found;
            const mins = parseInputToMinutes(value[0], is24hFormat);
            if (mins !== null) {
                const formattedLabel = formatMinutesToTime(mins, is24hFormat);
                return {
                    id: 'selected-value',
                    label: formattedLabel,
                    value: formattedLabel,
                    minutes: mins,
                };
            }
            return null;
        }, [value, options, is24hFormat]),
        scrollIntoView: () => void 0,
        onInputValueChange: ({ inputValue: __inputValue__, isOpen: __isOpen__ }) => {
            const newVal = __inputValue__ || '';
            setInputValue(newVal);
            // Restore cursor position after React re-render
            if (pendingCursorPosition.current !== null) {
                const cursorPos = pendingCursorPosition.current;
                requestAnimationFrame(() => {
                    if (anchorElement) {
                        anchorElement.setSelectionRange(cursorPos, cursorPos);
                    }
                });
                pendingCursorPosition.current = null;
            }
            if (__isOpen__) {
                // Find best match and scroll to center it
                const nextHighIndex = getHighlightedIndex(newVal, options, is24hFormat);
                if (nextHighIndex !== -1) {
                    setHighlightedIndex(nextHighIndex);
                    // Scroll with center alignment for Google Calendar-like behavior
                    rowVirtualizer.scrollToIndex(nextHighIndex, { align: 'center' });
                }
            }
        },
        onSelectedItemChange: ({ selectedItem }) => {
            if (selectedItem) {
                skipBlurValidation.current = true;
                const target = {
                    name,
                    value: [formatMinutesToHHmmss(selectedItem.minutes)],
                    label: selectedItem.label,
                };
                if (onChange)
                    onChange({ target });
                setInputValue(selectedItem.label);
                closeMenu();
                anchorElement?.blur();
            }
        },
        onIsOpenChange: ({ isOpen: __isOpen__, highlightedIndex: __highlightedIndex__ }) => {
            if (__onIsOpenChange__)
                __onIsOpenChange__({ isOpen: !!__isOpen__, highlightedIndex: __highlightedIndex__ });
            if (__isOpen__) {
                // When opening, highlight current value match if exists
                const indexToHighlight = getHighlightedIndex(inputValue, options, is24hFormat);
                if (indexToHighlight !== -1) {
                    setHighlightedIndex(indexToHighlight);
                    // Use 'center' alignment to show context before and after
                    rowVirtualizer.scrollToIndex(indexToHighlight, { align: 'center' });
                }
            }
        },
    });
    const handleInputFocus = useCallback(() => {
        openMenu();
    }, [openMenu]);
    useEffect(() => {
        // Keep input in sync with external value changes
        setInputValue(parseInitialValue(value, is24hFormat));
    }, [value, setInputValue]);
    const handleInputKeyDown = useCallback((event) => {
        const { key } = event;
        if (key === 'ArrowDown' || key === 'ArrowUp') {
            hasNavigatedWithArrows.current = true;
        }
        if (key === 'Enter') {
            event.preventDefault();
            if (hasNavigatedWithArrows.current) {
                const highlightedItem = isOpen && highlightedIndex >= 0 ? options[highlightedIndex] : null;
                if (highlightedItem) {
                    skipBlurValidation.current = true;
                    hasNavigatedWithArrows.current = false;
                    selectItem(highlightedItem);
                    return;
                }
            }
            const exactMatch = options.find((opt) => opt.label.toLowerCase() === inputValue.toLowerCase());
            if (exactMatch) {
                skipBlurValidation.current = true;
                hasNavigatedWithArrows.current = false;
                selectItem(exactMatch);
                return;
            }
            if (isValidTimeFormat(inputValue)) {
                const mins = parseInputToMinutes(inputValue, is24hFormat);
                const finalValue = mins !== null ? formatMinutesToHHmmss(mins) : inputValue;
                const displayValue = mins !== null ? formatMinutesToTime(mins, is24hFormat) : inputValue;
                skipBlurValidation.current = true;
                hasNavigatedWithArrows.current = false;
                if (onChange)
                    onChange({ target: { name, value: [finalValue] } });
                setInputValue(displayValue);
                closeMenu();
                anchorElement?.blur();
                return;
            }
            // Invalid time - revert to previous value and don't call onChange
            skipBlurValidation.current = true;
            hasNavigatedWithArrows.current = false;
            const currentFormattedValue = parseInitialValue(value, is24hFormat);
            setInputValue(currentFormattedValue);
            closeMenu();
            anchorElement?.blur();
        }
    }, [
        options,
        inputValue,
        name,
        onChange,
        closeMenu,
        anchorElement,
        selectItem,
        setInputValue,
        is24hFormat,
        isOpen,
        highlightedIndex,
    ]);
    const handleInputBlur = useCallback(() => {
        if (skipBlurValidation.current) {
            skipBlurValidation.current = false;
            return;
        }
        const currentFormattedValue = parseInitialValue(value, is24hFormat);
        if (inputValue === currentFormattedValue)
            return;
        if (!isValidTimeFormat(inputValue)) {
            setInputValue(currentFormattedValue);
            return;
        }
        const mins = parseInputToMinutes(inputValue, is24hFormat);
        if (mins !== null) {
            const finalValue = formatMinutesToHHmmss(mins);
            const displayValue = formatMinutesToTime(mins, is24hFormat);
            if (onChange)
                onChange({ target: { name, value: [finalValue] } });
            setInputValue(displayValue);
        }
        else {
            setInputValue(currentFormattedValue);
        }
    }, [inputValue, value, name, onChange, setInputValue, is24hFormat]);
    const handleSelectClear = useCallback(() => {
        setInputValue('');
        if (onChange)
            onChange({ target: { name, value: [] } });
    }, [name, onChange, setInputValue]);
    useEffect(() => {
        if (!isOpen) {
            hasNavigatedWithArrows.current = false;
        }
    }, [isOpen]);
    const isOptionSelected = useCallback((option) => option.minutes === selectedItem?.minutes, [selectedItem]);
    return (_jsx(BKJTimeInputContext.Provider, { value: {
            name,
            label,
            reset,
            value,
            error,
            action,
            isOpen,
            options,
            customTimeOption,
            isOptionSelected,
            variant,
            is24hFormat,
            onChange,
            required,
            disabled,
            openMenu,
            parentRef,
            closeMenu,
            inputValue,
            toggleMenu,
            placeholder,
            selectedItem,
            getItemProps,
            getMenuProps,
            getInputProps,
            getLabelProps,
            anchorElement,
            leftIconProps,
            leftIconName,
            rowVirtualizer,
            dropdownElement,
            setAnchorElement,
            highlightedIndex,
            getComboboxProps,
            isDropdownReverse,
            setIsDropdownReverse,
            setDropdownElement,
            portalTargetElement,
            pendingCursorPosition,
            setHighlightedIndex,
            getToggleButtonProps,
            onInputFocus: handleInputFocus,
            onInputBlur: handleInputBlur,
            onInputKeyDown: handleInputKeyDown,
            onSelectedClear: handleSelectClear,
        }, children: children }));
};
export const useBKJTimeInputContext = () => useContext(BKJTimeInputContext);
//# sourceMappingURL=BKJTimeInput.context.js.map